import { SelectedItem } from "01Data/02User/Locations";
import { AppContext } from "01Data/AppData";
import { Sensor } from "01Data/DataTypes";
import PageTemplate from "04UserPages/00PageTemplate/PageTemplate";
import {
  BreadCrumbItem,
  BreadCrumbList,
} from "04UserPages/99Parts/BreadCrumbList";
import { MapReader } from "97Parts/MapReader";
import { NoteText } from "97Parts/Styles";
import { useModel, useSignal } from "99Tools/useModel";
import { CheckKizaiID } from "99Tools/utils";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import { LIST_HEADER_COLOR } from "Settings";
import { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate, useSearchParams } from "react-router-dom";

const FormDlg = styled("form")(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%", // Fix IE 11 issue.
}));

const ErrorText = styled("div")(({ theme }) => ({
  color: "#f00",
}));

const ButtonDlg = styled(LoadingButton)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

const ColorHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: LIST_HEADER_COLOR,
  color: theme.palette.common.white,
}));

export const DetailFormPico3Sensor = ({ reload }: { reload: () => void }) => {
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pagePico3Devices;
  const s = p.selected.sensor;
  const redraw = useSignal();
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    p.list.bShowDetailDlg = false;
    s.clear();
    p.changed();
  };

  const handleChangeName = (e: any) => {
    p.selected.sensor.SensorName = e.target.value;
    s.errorMessage = "";
    redraw();
  };

  const handleChangeLocation = (e: any) => {
    p.selected.sensor.LocationName = e.target.value;
    s.errorMessage = "";
    redraw();
  };

  const handleApply = () => {
    if (p.selected.sensor.SensorName === "") {
      s.errorMessage = "表示名を入力してください。";
      redraw();
      return;
    }
    setLoading(true);
    d.EditPico3Sensor()
      .then(() => {
        p.list.bShowDetailDlg = false;
        p.sensors[p.selected.index] = p.selected.sensor;
        reload();
      })
      .catch((err: any) => {
        s.setError(err, "センサ設定変更に失敗しました。");
        d.pageDevices.changed();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!p.selected.IsSelected()) {
    return null;
  }

  return (
    <>
      <Dialog key="detail" open={p.list.bShowDetailDlg}>
        <FormDlg noValidate>
          <DialogTitle>詳細情報編集</DialogTitle>
          <DialogContent>
            <NoteText>付帯情報</NoteText>
            <TextField
              required
              variant="filled"
              margin="normal"
              fullWidth
              id={String("表示名")}
              label={"表示名"}
              name="name"
              inputProps={{ maxLength: 128 }}
              value={p.selected.sensor.SensorName}
              onChange={handleChangeName}
            />
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              id={String("設置場所")}
              label={"設置場所"}
              name="location"
              inputProps={{ maxLength: 128 }}
              value={p.selected.sensor.LocationName}
              onChange={handleChangeLocation}
            />
            <MapReader entity={s} redraw={redraw} />
            {s.errorMessage !== "" ? (
              <ErrorText>{s.errorMessage}</ErrorText>
            ) : null}
            <DialogActions>
              <ButtonDlg
                variant="contained"
                color="primary"
                onClick={handleApply}
                loading={loading}
              >
                適用
              </ButtonDlg>
              <ButtonDlg
                variant="contained"
                color="primary"
                onClick={handleClose}
              >
                キャンセル
              </ButtonDlg>
            </DialogActions>
          </DialogContent>
        </FormDlg>
      </Dialog>
    </>
  );
};

const columns = [
  {
    id: "id",
    label: "機材ID",
    minWidthPC: 100,
    minWidthSmartphone: "10vw",
  },
  { id: "name", label: "表示名", minWidthPC: 100, minWidthSmartphone: "10vw" },
  {
    id: "location",
    label: "設置場所",
    minWidthPC: 100,
    minWidthSmartphone: "10vw",
  },
  { id: "map", label: "詳細場所", minWidthPC: 100, minWidthSmartphone: "10vw" },
];

/**
 * Sensor一覧
 * @returns TSX
 */
const SensorBody = () => {
  const a = useContext(AppContext).account;
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pagePico3Devices;

  const handleDetailDlgOpen = (id: string) => {
    if (a.IsUserViewer()) {
      return;
    }
    const i = p.sensors.findIndex((s) => s.SensorId === id);
    p.list.bShowDetailDlg = true;
    p.selected = new SelectedItem(i, p.sensors[i]);
    p.changed();
  };

  const handleShowMap = (url: string) => {
    window.open(url, "_blank");
  };

  const rows = p.sensors
    .slice(
      p.list.page * p.list.rowsPerPage,
      p.list.page * p.list.rowsPerPage + p.list.rowsPerPage
    )
    .sort((a, b) => {
      return a.SensorName > b.SensorName ? 1 : -1;
    })
    .map((row: Sensor, i: number) => {
      return (
        <TableRow hover role="checkbox" tabIndex={-1} key={row.SensorId}>
          <TableCell
            key={columns[0].id + i}
            align="center"
            sx={a.IsUserViewer() ? null : { cursor: "pointer" }}
            onClick={() => handleDetailDlgOpen(row.SensorId)}
          >
            {row.SensorId.toString()}
          </TableCell>
          <TableCell
            key={columns[1].id + i}
            align="center"
            sx={a.IsUserViewer() ? null : { cursor: "pointer" }}
            onClick={() => handleDetailDlgOpen(row.SensorId)}
          >
            {row.SensorName}
          </TableCell>
          <TableCell
            key={columns[2].id + i}
            align="center"
            sx={a.IsUserViewer() ? null : { cursor: "pointer" }}
            onClick={() => handleDetailDlgOpen(row.SensorId)}
          >
            {row.LocationName}
          </TableCell>
          <TableCell key={columns[3].id + i} align="center">
            {row.SensorImage === "" ? (
              <Button variant="contained" disabled sx={{ textWrap: "nowrap" }}>
                表示
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => handleShowMap(row.SensorImage)}
                sx={{ textWrap: "nowrap" }}
              >
                表示
              </Button>
            )}
          </TableCell>
        </TableRow>
      );
    });

  return <TableBody>{rows}</TableBody>;
};

/**
 * Sensorテーブル
 * @returns TSX
 */
const SensorTable = () => {
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pagePico3Devices;

  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    p.list.page = newPage;
    p.changed();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    p.list.rowsPerPage = +event.target.value;
    p.list.page = 0;
    p.changed();
  };

  const header = columns.map((column) => (
    <ColorHeader
      key={column.id}
      align="center"
      style={{
        minWidth: isSmartphone ? column.minWidthSmartphone : column.minWidthPC,
      }}
    >
      {column.label}
    </ColorHeader>
  ));

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", marginTop: "20px" }}>
      <TableContainer sx={{ minHeight: "60vh" }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead style={{ whiteSpace: "nowrap" }}>
            <TableRow>{header}</TableRow>
          </TableHead>
          <SensorBody />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={p.sensors.length}
        rowsPerPage={p.list.rowsPerPage}
        page={p.list.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

const Pico3DevicePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const d = useContext(AppContext).dataUser.locations;
  const p = d.pagePico3Devices;
  useModel(p);

  const reload = () => {
    const id = searchParams.getAll("id");
    const building = searchParams.getAll("building");
    if (id.length === 0 || building.length === 0) {
      navigate("/buidings/");
    }
    const kind = CheckKizaiID(id[0]);
    if (kind !== "pico3") {
      navigate("/buildings/");
    }
    d.GetLocationAndDeviceData()
      .then(() => {
        const iBuilding = d.SearchLocation(building[0]);
        const iPico3 = d.SearchPico3(id[0]);
        if (iBuilding < 0 || iPico3 < 0) {
          navigate("/buildings/");
        }
        p.building = d.locations[iBuilding];
        p.pico3 = d.pico3[iPico3];
        p.sensors = d.GetPico3SensorList(building[0], id[0]);
        p.changed();
      })
      .catch((err: any) => {
        console.log("建物一覧取得処理失敗");
        console.error(err);
      });
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (p.pico3.PicoId === "") {
    return (
      <PageTemplate title="関連デバイス一覧">
        <BreadCrumbList
          items={[
            new BreadCrumbItem("TOP", "/top/"),
            new BreadCrumbItem("建物一覧", "/buildings/"),
            new BreadCrumbItem(p.building.BuildingName),
          ]}
        />
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title={`${p.pico3.PicoName} 関連デバイス一覧`}>
      <BreadCrumbList
        items={[
          new BreadCrumbItem("TOP", "/top/"),
          new BreadCrumbItem("建物一覧", "/buildings/"),
          new BreadCrumbItem(
            p.building.BuildingName,
            `/devices?building=${p.building.BuildingId}`
          ),
          new BreadCrumbItem(p.pico3.PicoName),
        ]}
      />
      <SensorTable />
      <DetailFormPico3Sensor reload={reload} />
    </PageTemplate>
  );
};

export default Pico3DevicePage;
